import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'

const TbxLoadingContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
}))

const TbxLoadingContent = styled(Stack)(() => ({
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    maxWidth: 400,
}))

/**
 * Simple, consistent loading overlay component following the Tbx design system.
 * Use this for basic loading states, route-level suspense fallbacks, and simple loading scenarios.
 *
 * For complex progress tracking with multiple steps, use the main TbxLoadingPage component.
 */
export const TbxLoadingOverlay = ({
    labelText = 'Loading...',
    progressColor = 'primary',
    showLabel = true,
    size = 36,
    textVariant = 'body1',
}) => {
    const { t } = useTranslation()

    const loadingLabelText = labelText || `${t('Loading')}...`

    return (
        <TbxLoadingContainer>
            <TbxLoadingContent spacing={3}>
                <CircularProgress
                    color={progressColor}
                    size={size}
                    thickness={4}
                />
                {showLabel ? (
                    <Typography
                        color="text.secondary"
                        variant={textVariant}
                    >
                        {loadingLabelText}
                    </Typography>
                ) : null}
            </TbxLoadingContent>
        </TbxLoadingContainer>
    )
}

TbxLoadingOverlay.propTypes = {
    labelText: PropTypes.string,
    progressColor: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'error', 'warning', 'info', 'success']),
    showLabel: PropTypes.bool,
    size: PropTypes.number,
    textVariant: PropTypes.string,
}

export default TbxLoadingOverlay
